import { useState } from 'react';
import { Tag } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { TagSearchField } from '@/settings/components/tag-search-field';
import { useSpecialityCodesSearch } from '@/settings/services';
import type { StaffSpeciality } from '@/types/settings-api';

/**
 * Shared SNOMED speciality typeahead + chip pills (PROD-788).
 *
 * Extracted from the practitioner Professional Details tab so the Staff
 * Account tab can consume the identical control instead of re-implementing
 * it (module-parity-audit: don't fork the picker per surface). Both the
 * practitioner `ProfessionalDetails.speciality` and the staff
 * `Staff.specialities` fields are `StaffSpeciality[]`, so one component
 * serves both — the consumer owns how the new array is merged back into
 * its own draft (the practitioner sets a single field; the staff routes
 * through `setStaffSpecialities` to keep `assignStaffToSpecialities` in
 * sync).
 *
 * Wired to `useSpecialityCodesSearch` (debounced 250ms — parity with
 * Languages #116 and Skills #117). Endpoint:
 *
 *   GET /api/Master/PracticeCodeList?searchQuery={q}&system={system}
 *
 * The `system` param is per-org config in Flutter
 * (`userData.resourceBasedIntegration.specialitySystem`). On the demo / dev
 * org it's unset → ships as empty string, which is what the captured cURL
 * showed. Thread the resolved value through the `system` prop when session
 * state carries it.
 *
 * Duplicate guard keys on `specialitySkillId` (master GUID, stable across
 * master refreshes). Master results match `StaffSpeciality` exactly, so a
 * selection is appended verbatim — no adapter.
 */
export interface SpecialityPickerProps {
  specialities: StaffSpeciality[];
  canEdit: boolean;
  onChange: (next: StaffSpeciality[]) => void;
  /** Per-org SNOMED system; defaults to empty (demo/dev org). */
  system?: string;
  /** Copy shown under the search input. */
  hint?: string;
  /** Copy shown when no speciality is attached. */
  emptyLabel?: string;
}

export function SpecialityPicker({
  specialities,
  canEdit,
  onChange,
  system = '',
  hint = 'Type to search and then add.',
  emptyLabel = 'At least one speciality is required.',
}: SpecialityPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 250);
  const { data: results = [], isLoading } = useSpecialityCodesSearch(
    debouncedSearch,
    system,
  );

  const attachedIds = new Set(
    specialities.map((s) => s.specialitySkillId).filter(Boolean),
  );
  const options = results
    .filter((r) => !attachedIds.has(r.specialitySkillId))
    .map((r) => ({
      id: r.specialitySkillId,
      label: r.code.displayName ?? r.code.code ?? r.specialitySkillId,
      secondary: r.code.code ?? undefined,
    }));

  const handleSelect = (specialitySkillId: string) => {
    const found = results.find((r) => r.specialitySkillId === specialitySkillId);
    if (!found) return;
    if (
      specialities.some((s) => s.specialitySkillId === found.specialitySkillId)
    ) {
      return;
    }
    onChange([
      ...specialities,
      {
        specialitySkillId: found.specialitySkillId,
        code: found.code,
      },
    ]);
    setSearch('');
    setOpen(false);
  };

  const handleRemove = (specialitySkillId: string) => {
    onChange(
      specialities.filter((s) => s.specialitySkillId !== specialitySkillId),
    );
  };

  return (
    <div className="space-y-3">
      {canEdit && (
        <TagSearchField
          value={search}
          placeholder="Enter speciality"
          hint={hint}
          open={open}
          onOpenChange={setOpen}
          onSearchChange={setSearch}
          loading={isLoading}
          options={options}
          onSelect={handleSelect}
        />
      )}

      {specialities.length === 0 ? (
        <p data-slot="speciality-empty">{emptyLabel}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {specialities.map((s) => (
            <Tag
              key={s.specialitySkillId}
              onRemove={
                canEdit ? () => handleRemove(s.specialitySkillId) : undefined
              }
              removeLabel={`Remove ${s.code.displayName ?? 'speciality'}`}
            >
              <span
                className="truncate max-w-70"
                title={s.code.displayName ?? undefined}
              >
                {s.code.displayName ?? s.code.code ?? 'Speciality'}
              </span>
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
}
